import React from 'react';
import PropTypes from 'prop-types';

//MUI
import {makeStyles} from 'tss-react/mui';

import {createTheme, ThemeProvider} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

//ICONOS
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const theme = createTheme();
//Definicion de estilo
const useStyles = makeStyles()((theme) => ({
   fullWidth: {
      width: '100% !important'
   },
   marginAuxT: {
      marginTop: '8px'
   },
   marginAuxL: {
      marginLeft: '8px'
   },
   bordeOutlined: {
      border: `1px solid #bcbdbf`,
      borderRadius: 'inherit'
   },
   chip: {
      margin: '4px'
   },
   chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      padding: '4px',
      minHeight: '54px',
      borderBottom: `1px solid #bcbdbf`
   },
   listChip: {
      display: 'flex',
      flexWrap: 'wrap',
      listStyle: 'none'
   },
   disabled: {
      opacity: 0.5,
      pointerEvent: 'none',
      background: '#CCC'
   },
   hidden: {
      display: 'none'
   }
}));

//Definicion del componente
function ChipList(props) {
   const {classes, cx} = useStyles();
   //Extraemos propiedades para facilitar su uso
   const {
      label,
      className,
      variant,
      fullWidth,
      disabled,
      chipProps,
      chips,
      genChipLabel,
      toolTipGenTitle,
      toolTipProps,
      AgregarIcon,
      onClickAgregar,
      agregarIconProps,
      onDeleteChip,
      onClickChip,
      hideChip
   } = props;

   //Separamos propiedades las cuales no pueden ser sobreescripta
   const {title, ...ttProps} = toolTipProps;
   const {onClick: onClickIcon, ...ibProps} = agregarIconProps;
   const {
      className: chipClassName,
      label: chipLabel,
      onDelete: onDeleteC,
      onClick: onClickC,
      ...cProps
   } = chipProps;

   return (
      <div
         className={cx(
            {
               [classes.fullWidth]: fullWidth,
               [classes.bordeOutlined]: variant === 'outlined',
               [classes.disabled]: disabled
            },
            classes.chipContainer,
            className
         )}
      >
         {label && (
            <Typography className={cx(classes.marginAuxL, classes.marginAuxT)}>{label}:</Typography>
         )}
         <div className={cx(classes.listChip)}>
            {chips.map((item, index) => {
               const chipPropsIndividual = {
                  label: genChipLabel(item),
                  className: cx(classes.chip, chipClassName),
                  onDelete: onDeleteChip ? () => onDeleteChip(item, index) : undefined,
                  onClick: onClickChip ? () => onClickChip(item, index) : undefined,
                  ...cProps
               };
               const hidden = hideChip ? hideChip(item, index) : false;
               return (
                  <div key={index} hidden={hidden}>
                     {toolTipGenTitle ? (
                        <Tooltip title={toolTipGenTitle(item)} {...ttProps}>
                           <Chip {...chipPropsIndividual} />
                        </Tooltip>
                     ) : (
                        <Chip {...chipPropsIndividual} />
                     )}
                  </div>
               );
            })}
            {onClickAgregar && (
               <div className={classes.chip}>
                  <IconButton
                     onClick={onClickAgregar}
                     {...ibProps}
                     size="large"
                     {...ibProps}
                     style={{paddingTop: '0px'}}
                  >
                     <AgregarIcon />
                  </IconButton>
               </div>
            )}
         </div>
      </div>
   );
}

ChipList.propTypes = {
   label: PropTypes.string,
   /** Propiedades de los [chip](https://material-ui.com/api/chip/ "Material UI Chip API") individuales. */
   chipProps: PropTypes.object,
   /** Lista de los objetos mostrados como chips. */
   chips: PropTypes.arrayOf(PropTypes.object),
   /** Función generadora del label de los chips con la siguiente firma:
    *
    * - `chip`: objeto, con la misma forma a los del array de la propiedad chips.
    * - `retorno`: string, el texto que se desea mostrar en el chip.
    * */
   genChipLabel: PropTypes.func,
   /** Funcion generadora de la propiedad title de los [tooltip](https://material-ui.com/components/tooltips/ "Material UI ToolTip API"), con la siguiente firma:
    *
    * - `chip`: objeto, con la misma forma a los del array de la propiedad chips.
    * - `retorno`: string, texto a mostrar en el tooltip.
    *
    * Si no es proporcionada no se usaran toolTips. */
   toolTipGenTitle: PropTypes.func,
   /** Propiedades de los [tooltip](https://material-ui.com/api/tooltip/ "Material UI ToolTip API") individuales con ecepcion de la propiedad title. */
   toolTipProps: PropTypes.object,
   /** Funcion a ejecutar cuando se hace click en el IconButton agregar, si no es proporcionado no se mostrara un boton agregar. Con firma especificada por [eventos sinteticos de react](https://reactjs.org/docs/events.html)
    */
   onClickAgregar: PropTypes.func,
   /** Icono a usar en el boton agregar. */
   AgregarIcon: PropTypes.elementType,
   /** Propiedades de los [chip](https://material-ui.com/api/icon-button/ "Material UI IconButton API") individuales con ecepcion de la propiedad title. */
   agregarIconProps: PropTypes.object,
   /** Variante a usar, corresponde a las mismas variantes del componente TextField de Material-UI */
   variant: PropTypes.oneOf(['outlined', 'standard']),
   /** Si es verdadero el componente ocupara todo el ancho de su contenedor. */
   fullWidth: PropTypes.bool,
   /** Si es verdadero el componente estara deshabilitado. */
   disabled: PropTypes.bool,
   /** Accion a ejecutar cuando se hace click sobre un chip, con la siguiente firma:
    *
    * - `chip`: int, indice del objeto en el arreglo de chips.
    * - `indice`: valor del chip.
    * */
   onClickChip: PropTypes.func,
   /** Accion a ejecutar cuando se quiere eliminar un chip,con la siguiente firma:
    *
    * - `chip`: int, indice del objeto en el arreglo de chips.
    * - `indice`: valor del chip.
    * */
   onDeleteChip: PropTypes.func,
   /** Funcion para determinar si un chip es visible, con la siguiente firma:
    *
    * - `chip`: valor del chip.
    * - `indice`: int, indice del objeto en el arreglo de chips.
    * - `retorno`: boolean, Si el resultado de la función es __TRUE__ el chip __NO__ sera visible.
    * */
   hideChip: PropTypes.func
};
ChipList.defaultProps = {
   chipProps: {
      clickable: true
   },
   chips: [
      {value: 0, label: 'chip 0'},
      {value: 1, label: 'chip 1'}
   ],
   genChipLabel: function (chip) {
      return chip.label;
   },
   toolTipProps: {
      arrow: true
   },
   variant: 'standard',
   agregarIconProps: {
      size: 'small'
   },
   fullWidth: false,
   disabled: false,
   AgregarIcon: AddCircleOutlineIcon,
   hideChip: function (index, chip) {
      return false;
   }
};

export default ChipList;
